import React, { useState } from "react";
import { X, Ban, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

export default function SuspendDialog({ target, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    await onConfirm(reason.trim());
    setSubmitting(false);
  };

  return (
    <div
      data-testid="admin-suspend-dialog"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center"
          data-testid="admin-suspend-dialog-close"
        >
          <X className="w-4 h-4 text-zinc-500" />
        </button>

        <div className="bg-gradient-to-br from-red-600 to-red-800 text-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Ban className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-red-200">
                Suspender servicio
              </p>
              <h3 className="text-lg font-black">Bloquear cuenta del usuario</h3>
            </div>
          </div>
          <p className="text-xs text-red-100 leading-snug">
            Esta acción revoca los créditos otorgados por este pago y bloquea el PIN del usuario.
            Solo úsala si la referencia es inválida o sospechosa.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-zinc-50 rounded-xl p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-zinc-500">Usuario:</span>
              <span className="font-bold text-zinc-900">{target.username}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">PIN:</span>
              <span className="font-mono font-black text-zinc-900">{target.user_pin || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Plan:</span>
              <span className="font-bold text-zinc-900">{target.plan_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Referencia:</span>
              <span className="font-mono text-zinc-900 truncate max-w-[60%]">
                {target.reference || target.mtcn || "—"}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-sm font-bold text-zinc-900 mb-1.5 block">
              Motivo (opcional pero recomendado)
            </Label>
            <Input
              data-testid="admin-suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Referencia no apareció en banco después de 48h"
              className="h-12 text-sm rounded-xl border-2 border-zinc-300 focus:border-red-600"
              autoFocus
            />
          </div>

          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-900 leading-snug">
              El usuario no podrá entrar con su PIN. Si fue un error, puedes reactivarlo después desde la sección «Suspender PIN manual».
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={onClose}
              disabled={submitting}
              variant="outline"
              className="flex-1 h-12 font-bold rounded-xl"
              data-testid="admin-suspend-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className="flex-1 h-12 font-black bg-red-600 hover:bg-red-700 text-white rounded-xl"
              data-testid="admin-suspend-confirm"
            >
              {submitting ? "Suspendiendo…" : "Suspender servicio"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
